import React, {useEffect, useState} from 'react';
import {Chip, Tooltip, Stack, Typography} from '@mui/material';
import {Brightness1} from '@mui/icons-material';

import {hexToRgba} from '../../helpers/ColorUtils';
import {toTitleCase} from '../../helpers/TextUtils';
import {determineCapLevelHelper} from '../../helpers/DetermineCapLevelHelper';
import {DEFAULT_CAP_LEVEL_COLOR_GREY} from '../../constants/SessionConstants';
import LaunchIconLink from "./LaunchIconLink";

const CONSTANTS = {
  DEFAULT_VALUE_FOR_NONE: '-',
};

const formatToCapitalized = (value) => {
  if (!value) {
    return CONSTANTS.DEFAULT_VALUE_FOR_NONE;
  }
  return toTitleCase(value);
};

const ContentTypeDisplay = ({ contentType }) => {
  const formattedContentType = formatToCapitalized(contentType);

  return (
    <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>
      Content Type: {formattedContentType}
    </Typography>
  );
};

const ContentNameAndLinkDisplay = ({ names, links = [] }) => {
  const capitalizedContentNames = names.map((contentName) => formatToCapitalized(contentName));

  return <Stack direction="row" alignItems="center" spacing={1}>
    <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>
      <span style={{ marginRight: '0.25rem' }}>Content Name:</span>
      {capitalizedContentNames.length > 1 ? (
        <ul style={{ margin: 0, paddingLeft: '1rem' }}>
          {capitalizedContentNames.map((contentName, index) => (
            <li key={index}>
              {contentName}
              <LaunchIconLink link={links[index]} />
            </li>
          ))}
        </ul>
      ) : (
        <>
          {capitalizedContentNames[0] || CONSTANTS.DEFAULT_VALUE_FOR_NONE}
          <LaunchIconLink link={links[0]} />
        </>
      )}
    </Typography>
  </Stack>;
};

export default function SessionContentChip({ content }) {
  const [capLevelName, setCapLevelName] = useState('');
  const [capLevelColor, setCapLevelColor] = useState(DEFAULT_CAP_LEVEL_COLOR_GREY);

  const capLevelChipStyle = {
    flexShrink: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    minWidth: 'inherit',
  };

  useEffect(() => {
    const { name, color } = determineCapLevelHelper(content);
    setCapLevelName(name);
    setCapLevelColor(color);
  }, [content]);

  const uniqueContentTypes = Array.isArray(content)
    ? [...new Set(content.map(c => c.contentType))]
    : [content?.contentType];

  const contentNames = Array.isArray(content)
    ? content.map(c => c.name)
    : [content?.name];

  const contentLinks = Array.isArray(content)
    ? content.map(c => c.externalUrl || '')
    : [content?.externalUrl || ''];

  return (
    <Tooltip
      title={
        <Stack spacing={0.5} sx={{ padding: '0.25rem', maxWidth: '11.25rem' }}>
          {uniqueContentTypes.map((contentType, index) => (
            <ContentTypeDisplay key={index} contentType={contentType} />
          ))}
          <ContentNameAndLinkDisplay names={contentNames} links={contentLinks} />
        </Stack>
      }
      sx={{
        '& .MuiTooltip-tooltip': {
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
        },
      }}
    >
      <Chip
        size="medium"
        icon={<Brightness1 sx={{ '&&': { color: hexToRgba(capLevelColor, 1), fontSize: '1.2rem'} }} />}
        sx={capLevelChipStyle}
      />
    </Tooltip>
  );
}